import React, { useState } from 'react';
import styled from 'styled-components';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// Wrapper for the entire Case Studies section
const CaseStudiesWrapper = styled.section`
  padding: 50px 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

// Title styling for "Case Studies"
const Title = styled.h1`
  font-size: 3rem;
  text-align: center;
  margin-bottom: 50px;
  color: #0070c9; /* Accent color for title */
  font-weight: bold;
`;

// Case Study card styling
const CaseStudyCard = styled.div`
  background-color: #f9f9f9;
  border-radius: 15px;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.1); /* Softer shadow for depth */
  padding: 30px;
  cursor: pointer;
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 250px;

  &:hover {
    transform: translateY(-10px); /* Lift effect on hover */
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2); /* Stronger shadow on hover */
  }
`;

// Case Study Title styling
const CaseStudyTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
  transition: color 0.3s ease;

  &:hover {
    color: #0070c9; /* Apple-like blue on hover */
  }
`;

// Case Study Description styling
const CaseStudyDescription = styled.p`
  font-size: 1.1rem;
  color: #666;
`;

// Modal overlay styling
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99;
`;

// Modal content styling
const ModalContent = styled.div`
  background-color: #ffffff;
  padding: 40px;
  border-radius: 15px;
  max-width: 600px;
  width: 90%;
  max-height: 80%;
  overflow-y: auto;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.3);
  position: relative;
  z-index: 100;
`;

// Close button styling
const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 2rem;
  position: absolute;
  top: 20px;
  right: 20px;
  cursor: pointer;
  color: #555;

  &:hover {
    color: #ff0000;
  }
`;

// Slider settings for responsiveness
const settings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 3, // Show 3 cards at a time for large screens
  slidesToScroll: 1,
  arrows: true,
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 2, // Show 2 cards for medium screens (tablets)
        slidesToScroll: 1,
      }
    },
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 1, // Show 1 card for small screens (mobiles)
        slidesToScroll: 1,
      }
    }
  ]
};

const CaseStudies = () => {
  const [selectedCaseStudy, setSelectedCaseStudy] = useState(null);

  const caseStudies = [
    {
      title: "AWS Consulting - Global eCommerce Platform",
      shortDescription: "How we helped a Fortune 500 company deploy a scalable global eCommerce platform.",
      detailedDescription: `
        We architected and deployed a multi-region, high-availability eCommerce platform for a Fortune 500 retail company. 
        Utilizing services like Amazon ECS, S3, and RDS, the system now supports millions of daily transactions with 99.99% uptime. 
        The platform's auto-scaling capabilities allow for dynamic scaling during high-traffic periods, reducing manual intervention.
        This solution reduced operational costs by 35%, while enhancing customer experience through improved response times.
      `
    },
    {
      title: "Azure Consulting - Healthcare Cloud Migration",
      shortDescription: "Migrating a healthcare provider's infrastructure to Azure, improving scalability and compliance.",
      detailedDescription: `
        We led the cloud migration for a healthcare provider, moving their sensitive patient data and core applications to Azure. 
        Using Azure Security Center and Azure Key Vault, we ensured that all patient information remained secure and compliant with HIPAA standards. 
        This migration improved data accessibility by 40%, reduced on-premise hardware costs by 50%, and allowed for greater scalability 
        in handling patient records and telemedicine services.
      `
    },
    {
      title: "Google Cloud Consulting - IoT Data Processing",
      shortDescription: "Real-time IoT data processing system for a global manufacturer using Google Cloud.",
      detailedDescription: `
        We designed and implemented a real-time IoT data processing system for a global manufacturer using Google Cloud Pub/Sub and BigQuery. 
        The system processes data from over 1 million devices in real-time, providing actionable insights on equipment performance and enabling predictive maintenance. 
        The solution reduced unplanned downtime by 20% and improved production efficiency through automated reporting and advanced analytics.
      `
    },
    {
      title: "DevOps - CI/CD Pipeline Automation",
      shortDescription: "Automated CI/CD pipelines for a logistics firm, reducing deployment times significantly.",
      detailedDescription: `
        We implemented fully automated CI/CD pipelines for a logistics firm using AWS CodePipeline and Terraform. 
        This automation reduced deployment times from hours to minutes and improved the overall release quality by incorporating automated testing and rollback features, 
        reducing human errors by 30%. The client saw faster feature delivery and fewer bugs in production.
      `
    },
    {
      title: "Security & Compliance - Financial Services Security",
      shortDescription: "Deployed a multi-layered security framework for a financial services provider, ensuring compliance and reducing risk.",
      detailedDescription: `
        For a financial services provider, we deployed a multi-layered security framework using AWS Shield, AWS GuardDuty, and encryption services. 
        The framework provided real-time threat detection, incident response, and compliance with PCI-DSS standards. 
        The solution reduced the client’s vulnerability to cyberattacks by 90% and ensured ongoing compliance with industry standards.
      `
    }
  ];

  const handleClickOutside = (e) => {
    if (e.target.id === "modal-overlay") {
      setSelectedCaseStudy(null);
    }
  };

  return (
    <CaseStudiesWrapper>
      <Title>Case Studies</Title>
      {selectedCaseStudy && (
        <ModalOverlay id="modal-overlay" onClick={handleClickOutside}>
          <ModalContent>
            <CloseButton onClick={() => setSelectedCaseStudy(null)}>&times;</CloseButton>
            <h2>{selectedCaseStudy.title}</h2>
            <p>{selectedCaseStudy.detailedDescription}</p>
          </ModalContent>
        </ModalOverlay>
      )}
      {!selectedCaseStudy && (
        <Slider {...settings}>
          {caseStudies.map((caseStudy, index) => (
            <div key={index}>
              <CaseStudyCard onClick={() => setSelectedCaseStudy(caseStudy)}>
                <CaseStudyTitle>{caseStudy.title}</CaseStudyTitle>
                <CaseStudyDescription>{caseStudy.shortDescription}</CaseStudyDescription>
              </CaseStudyCard>
            </div>
          ))}
        </Slider>
      )}
    </CaseStudiesWrapper>
  );
};

export default CaseStudies;
